import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Grid, Button, CircularProgress } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import { getConnections } from '@wagmi/core';
import { toast } from 'react-toastify';
import { useAccount } from 'wagmi';
import Web3 from 'web3';
import { CiWallet } from 'react-icons/ci';
import { IoMdCheckmarkCircleOutline } from 'react-icons/io';
import { MdOutlineErrorOutline } from 'react-icons/md';

import TokenDistributorAbi from '../../ContractABI/TokenDistributor.json';
import ConnectButton from '../../WagmiProvider/ConnectButton';
import TopNavBar from '../../Components/TopBar/Navbar';
import { useUserContext } from '../../context/user.context';
import { useAirdrop } from '../Airdrop/useAirdrop';
import { wagmiAdapterConfig } from '../../Config/WagmiConfig';
import { AppConfig } from '../../Config/app-config';
import {
  REACT_APP_CHAIN_ID,
  REACT_APP_ETH_TRANSACTION,
  REACT_APP_TOKEN_DISTRIBUTOR,
  REACT_APP_AIRDROP_START_DATE,
  REACT_APP_AIRDROP_END_DATE,
  REACT_APP_ETH_RPC,
} from '../../Config/Config';
import CustomModal from '../../Components/Modal/CustomModal';
import GradiantButton from '../../Components/Button/GradiantButton';

const AirdropClaim = () => {
  const { airdropData } = useUserContext();
  const { isEligible, airdropTokens, claimed, setClaimed } = useAirdrop();
  const [isChecked, setIsChecked] = useState(false);
  const [openClaimModal, setOpenClaimModal] = useState(false);
  const [claimStatus, setClaimStatus] = useState('Waiting');
  const [isCompleted, setIsCompleted] = useState(false);
  const [txHash, setTxHash] = useState('');
  const { isConnected, address } = useAccount();
  const startDate = REACT_APP_AIRDROP_START_DATE;
  const endDate = REACT_APP_AIRDROP_END_DATE;

  const formatUTCStartDate = useMemo(() => {
    if (!startDate) return '';
    const date = new Date(Number(startDate) * 1000);
    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'UTC',
    };

    return date.toLocaleString('en-GB', options).replace(',', '');
  }, [startDate]);

  const formatUTCEndDate = useMemo(() => {
    if (!endDate) return '';
    const date = new Date(Number(endDate) * 1000);
    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'UTC',
    };

    return date.toLocaleString('en-GB', options).replace(',', '');
  }, []);

  const checkClaimed = useCallback(async () => {
    if (!address) {
      return;
    }

    try {
      const web3 = new Web3(REACT_APP_ETH_RPC);
      const tokenDistributorContract = new web3.eth.Contract(
        TokenDistributorAbi,
        REACT_APP_TOKEN_DISTRIBUTOR,
      );

      await tokenDistributorContract.methods
        .claim(airdropData.tokenAmount, airdropData.proofs)
        .estimateGas({ from: address });

      setClaimed(false);
    } catch (error) {
      if (airdropData.eligible === false) {
        setClaimed(false);
      } else {
        setClaimed(true);
      }
      console.error('Error checking claimed status:', error);
    }
  }, [address, airdropData]);

  useEffect(() => {
    if (address && airdropData) {
      checkClaimed();
    }
  }, [address, airdropData]);

  const connections = getConnections(wagmiAdapterConfig);

  const handleClaim = async () => {
    if (!connections || connections.length === 0) {
      toast.warn('No wallet connector found');
      return;
    }

    const walletProvider = await connections[0].connector.getProvider();

    if (!walletProvider) {
      toast.warn('Wallet provider not found');
      return;
    }

    if (connections[0].chainId !== REACT_APP_CHAIN_ID) {
      toast.warn(
        `Switch Your Network To ${AppConfig.isDev ? 'Sepolia testnet' : 'Ethereum Mainnet'}`,
      );
      return;
    }

    if (!isEligible) {
      toast.warn('Wallet not eligible');
      return;
    }

    if (!isChecked) {
      toast.warn('You must agree to OZK Airdrop Terms and Conditions');
      return;
    }

    const web3 = new Web3(walletProvider);

    const tokenDistributorContract = new web3.eth.Contract(
      TokenDistributorAbi,
      REACT_APP_TOKEN_DISTRIBUTOR,
    );

    try {
      setOpenClaimModal(true);
      setClaimStatus('Loading');

      const tx = await tokenDistributorContract.methods
        .claim(airdropData.tokenAmount, airdropData.proofs)
        .send({ from: address });

      setClaimed(true);
      setTxHash(tx.transactionHash);
      setIsCompleted(true);
      setClaimStatus('Completed');
    } catch (error) {
      console.error(error);
      setIsCompleted(false);
      setClaimStatus('Error');
    }
  };

  return (
    <>
      <Grid
        container
        gap="50px"
        display={'flex'}
        flexDirection={'column'}
        justifyContent={'start'}
        className="leaderboard_page"
      >
        <Grid container zIndex={100}>
          <TopNavBar />
        </Grid>

        <div className="d-flex flex-col items-center gap-10 justify-center section-container">
          <Grid
            className="pb-0 px-0 py-0 "
            lg={6}
            md={6}
            sm={12}
            xs={12}
            display="flex"
            flexDirection="column"
            gap={'40px'}
          >
            <Grid
              item
              lg={9}
              md={10}
              sm={10}
              xs={10}
              display={'flex'}
              flexDirection={'column'}
              className="mx-auto"
              gap={4}
            >
              <p className="landingHeading text-center">
                Airdrop Phase 1 Claim
              </p>
            </Grid>

            <div
              className="p-3 pb-0 d-flex flex-col w-full mx-auto"
              style={{
                background:
                  'linear-gradient(0deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.08)),linear-gradient(90deg, rgba(254, 221, 69, 0.12) 0%, rgba(255, 233, 133, 0.12) 30.9%, rgba(252, 157, 75, 0.12) 65.4%, rgba(250, 116, 43, 0.12) 100%)',
                borderRadius: '25px',
              }}
            >
              <Grid
                container
                display={'flex'}
                flexDirection={'column'}
                className="gap-3"
              >
                <Grid
                  item
                  xs={12}
                  display={'flex'}
                  flexDirection={'column'}
                  alignItems={'center'}
                  justifyContent={'center'}
                  className="w-full"
                >
                  {isConnected ? (
                    isEligible && (
                      <p className="claimTextStyle">
                        You are eligible to claim from the OZK Airdrop:
                      </p>
                    )
                  ) : (
                    <p className="claimTextStyle">
                      Check OZK Airdrop eligibility and number of OZK tokens
                      able to be claimed
                    </p>
                  )}
                </Grid>
                <Grid
                  item
                  // xs={12}
                  display={'flex'}
                  flexDirection={'row'}
                  alignItems={'center'}
                  justifyContent={'center'}
                >
                  <Grid item display={'flex'} alignItems={'center'}>
                    {isConnected ? (
                      <div className="mt-2 mb-1">
                        {isEligible ? (
                          claimed ? (
                            <p className="claimTextStyle">
                              <span className="claimableOzkStyle">NO</span> OZK
                              tokens to claim from the OZK Airdrop.{' '}
                            </p>
                          ) : (
                            <p className="claimTextStyle">
                              <span className="claimableOzkStyle">
                                {airdropTokens}
                              </span>{' '}
                              OZK tokens available to claim from{' '}
                              <span className="text-orange64">
                                {formatUTCStartDate} UTC
                              </span>{' '}
                              to{' '}
                              <span className="text-orange64">
                                {formatUTCEndDate} UTC
                              </span>
                            </p>
                          )
                        ) : (
                          <p className="notEligible">
                            Connected wallet is not eligible for the OZK Airdrop
                          </p>
                        )}
                      </div>
                    ) : (
                      <ConnectButton />
                    )}
                  </Grid>
                </Grid>
                {isConnected && airdropTokens > 0 && !claimed && (
                  <Grid
                    item
                    xs={12}
                    display={'flex'}
                    flexDirection={'row'}
                    alignItems={'center'}
                    justifyContent={'center'}
                  >
                    <Grid item display={'flex'}>
                      <p className="agreeClaimTextStyle">
                        <Checkbox
                          style={{ color: 'green' }}
                          checked={isChecked}
                          onClick={() => setIsChecked((prev) => !prev)}
                        />{' '}
                        Agree to the OZK Airdrop
                        <a
                          href="/pdf/OpenZK - Airdrop Campaign Claim Terms.pdf"
                          target="_blank"
                          className="text-orange64"
                        >
                          {' '}
                          Terms and Conditions{' '}
                        </a>
                      </p>
                    </Grid>
                  </Grid>
                )}
                {!claimed && isConnected && isEligible && (
                  <Grid
                    item
                    className="w-100"
                    xs={12}
                    display={'flex'}
                    flexDirection={'column'}
                    alignItems={'center'}
                    justifyContent={'center'}
                    gap={1}
                  >
                    <Button
                      className="mb-0"
                      sx={{
                        height: '40px',
                        width: 'auto',
                        backgroundColor: '#000000',
                        border: 0.75,
                        borderColor: '#FC9D4B',
                        borderRadius: '10px',
                        color: '#FFE584',
                        fontWeight: 'semibold',
                        fontFamily: 'PoppinsRegular',
                        fontSize: {
                          xs: '10px', // For extra small screens (mobile)
                          sm: '16px', // For small screens (tablets)
                          md: '18px', // For medium screens
                          lg: '18px', // For large screens
                        },
                        padding: {
                          xs: '15px', // For extra small screens
                          sm: '12px', // For small screens
                          md: '15px', // For medium screens
                        },
                        '&:hover': {
                          background:
                            'linear-gradient(90deg, rgba(241, 215, 75, 0.8) 0%, rgba(221, 113, 42, 0.8) 65.9%)',
                          color: '#ffffff',
                        },
                        textTransform: 'none',
                      }}
                      onClick={() => handleClaim()}
                    >
                      Claim
                    </Button>
                    {/* <p className="claimTextStyle">
                      Click{' '}
                      <a href="" target="blank" className="text-orange64">
                        here
                      </a>{' '}
                      for more info on Claiming OZK
                    </p> */}
                  </Grid>
                )}
              </Grid>
            </div>
          </Grid>
        </div>
      </Grid>
      <CustomModal
        open={openClaimModal}
        closebtn={'remove'}
        modalTitle={<h5>Wallet Actions</h5>}
        width={{ lg: '30%', md: '30%', xs: '90%', sm: '90%' }}
        modalContent={
          <Grid
            container
            lg={12}
            display={'flex'}
            flexDirection={'row'}
            alignItems={'center'}
            justifyContent={'center'}
          >
            <Grid
              container
              lg={12}
              display={'flex'}
              flexDirection={'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
              className="p-2 rounded"
              bgcolor={'rgba(0, 0, 0, 0.32)'}
            >
              <Grid className="d-flex">
                <p>
                  {' '}
                  <CiWallet className="text-light mx-2" size={30} /> Claim in
                  progress
                </p>
              </Grid>

              {claimStatus === 'Waiting' ? (
                <span style={{ color: '#ffffff' }}>Waiting</span>
              ) : claimStatus === 'Loading' ? (
                <span>
                  <CircularProgress style={{ color: '#FFF' }} size={20} />
                </span>
              ) : claimStatus === 'Completed' ? (
                <p>
                  {' '}
                  <IoMdCheckmarkCircleOutline
                    style={{ color: '#FC9D4B' }}
                    size={20}
                  />
                </p>
              ) : (
                <p>
                  <MdOutlineErrorOutline
                    style={{ color: '#FC9D4B' }}
                    size={20}
                  />
                </p>
              )}
            </Grid>

            <Grid
              container
              lg={12}
              display={'flex'}
              flexDirection={'row'}
              alignItems={'center'}
              justifyContent={'center'}
              className="mt-4 w-100 "
            >
              {isCompleted ? (
                <p
                  className="airdrop_paratext mb-3 p-3"
                  style={{
                    border: '1px solid #ffffff',
                    borderRadius: '25px',
                    textAlign: 'center',
                  }}
                >
                  {`${claimStatus === 'Error' ? 'Error encountered during contract execution' : 'Claim successful'}`}{' '}
                </p>
              ) : (
                claimStatus === 'Error' && (
                  <p
                    className="airdrop_paratext mb-3 p-3"
                    style={{
                      border: '1px solid #ffffff',
                      borderRadius: '25px',
                      textAlign: 'center',
                    }}
                  >
                    Error encountered during contract execution
                  </p>
                )
              )}{' '}
              {txHash !== '' ? (
                <>
                  <a
                    href={REACT_APP_ETH_TRANSACTION + txHash}
                    target="blank"
                    style={{
                      color: '#fff',
                      width: '70%',
                      marginBottom: '20px',
                    }}
                  >
                    <GradiantButton
                      btnTextColor="#FFF"
                      text={'CHECK ETHERSCAN'}
                      width="100%"
                      onClick={undefined}
                      style={undefined}
                      btnColor={undefined}
                      Icon={undefined}
                      disabled={false}
                      endIcon={undefined}
                      height={undefined}
                      isUpolad={undefined}
                      FileUpload={undefined}
                      Filetype={undefined}
                    />
                  </a>

                  <GradiantButton
                    btnTextColor="#FFF"
                    text={'CLOSE'}
                    width="70%"
                    onClick={() => {
                      {
                        setOpenClaimModal(false);
                        setTxHash('');
                        setIsCompleted(false);
                        setClaimStatus('Waiting');
                      }
                    }}
                    style={undefined}
                    btnColor={undefined}
                    Icon={undefined}
                    disabled={false}
                    endIcon={undefined}
                    height={undefined}
                    isUpolad={undefined}
                    FileUpload={undefined}
                    Filetype={undefined}
                  />
                </>
              ) : (
                <GradiantButton
                  btnTextColor="#FFF"
                  text={!isCompleted ? 'CLOSE' : 'CHECK ETHERSCAN'}
                  width="100%"
                  onClick={() => {
                    setOpenClaimModal(false);
                    setTxHash('');
                    setIsCompleted(false);
                    setClaimStatus('Waiting');
                  }}
                  style={undefined}
                  btnColor={undefined}
                  Icon={undefined}
                  disabled={false}
                  endIcon={undefined}
                  height={undefined}
                  isUpolad={undefined}
                  FileUpload={undefined}
                  Filetype={undefined}
                />
              )}
            </Grid>
          </Grid>
        }
        setOpen={undefined}
        transparent={undefined}
        height={undefined}
        scrollable={undefined}
      />
    </>
  );
};

export default AirdropClaim;
